import React, { useCallback, useEffect, useMemo } from "react";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { replace } from "react-router-redux";

import { TransferConfirmation, resolveTransferTitleKey, Spinner, transfersTestIds } from "@ui-kit/components/index.es";
import { resizableRoute } from "pages/_components/Resizable";

import { actions as formActions, selectors as formSelectors } from "reducers/form";
import { actions as layoutActions } from "reducers/layout";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as transferActions, selectors as transferSelectors } from "reducers/transfer";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";

import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";
import isTokenActive from "util/token";
import { getTransferUxLabel, isTransferUxI18nKey } from "util/transferInternalTitleLabels";
import { resolveTransferFormGoBackPath } from "util/transfers";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getTransferExecutePath } from "util/v3UxConfig";

import { buildTransferDataInput, getTransferConfirmationNotes } from "./transferTicketData";

const TRANSFER_CONFIRMATION_SCOPES = ["form"];

/**
 * Resolves the transfer type used for the back link. Before the transaction exists there is no
 * `idActivity`, so the flow flags are the only source of truth.
 */
function resolveTransferType(internalTransfer, transferGroup) {
    if (transferGroup === "toContact") {
        return "toContact";
    }
    return internalTransfer ? "internal" : "thirdParties";
}

/**
 * V3 **review + confirm** screen for a transfer. Mirrors legacy `/transfer/summary`: it shows the
 * data the user is about to submit and collects the OTP, then delegates to the same send actions.
 */
function TransferConfirmationV3Page(props) {
    const {
        dispatch,
        summary,
        internalTransfer,
        transferGroup,
        isCVU,
        userId,
        envName,
        envDocument,
        isSubmitting,
        accounts,
        signTransactionData,
        cancelTransactionData,
        location,
        isDesktop,
        tokenNumber,
        fetchingToken,
    } = props;

    const isMobileWithToken = !isDesktop && isTokenActive();

    const transferType = resolveTransferType(internalTransfer, transferGroup);
    const formPath = getTransferExecutePath(transferType);
    const isCancelFlow = summary?.action === "cancel";
    const isSignFlow = summary?.action === "sign";
    const goBackPath = useMemo(() => {
        const isSignOrCancel = isSignFlow || isCancelFlow;
        if (
            isSignOrCancel &&
            typeof location?.state?.goBackState === "string" &&
            location.state.goBackState.startsWith("/")
        ) {
            return location.state.goBackState;
        }
        // Preserve form data when going back from confirmation (reset only via explicit `/back`).
        return resolveTransferFormGoBackPath({ internalTransfer, transferGroup });
    }, [isSignFlow, isCancelFlow, location, internalTransfer, transferGroup]);

    useEffect(() => {
        if (!summary?.debitAccount) {
            dispatch(replace(formPath));
        }
    }, [dispatch, summary, formPath]);

    useEffect(() => {
        if (isMobileWithToken) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
    }, [dispatch, isMobileWithToken]);

    const getLabels = useCallback((id) => i18n.get(id), []);

    const title = useMemo(() => {
        if (summary?.idActivity) {
            const titleKey = resolveTransferTitleKey(summary.idActivity);
            if (isTransferUxI18nKey(titleKey, "transfers.summary.internal.label")) {
                return getTransferUxLabel("transfers.summary.internal.label", "Transferir a mis cuentas");
            }
            return i18n.get(titleKey);
        }
        if (transferGroup === "toContact") {
            return i18n.get("transfers.summary.toContact.label");
        }
        if (internalTransfer) {
            return getTransferUxLabel("transfers.summary.internal.label", "Transferir a mis cuentas");
        }
        return i18n.get("transfers.summary.external.label");
    }, [summary, internalTransfer, transferGroup]);

    const transferData = useMemo(
        () =>
            buildTransferDataInput({
                summary,
                internalTransfer,
                isCVU,
                transferGroup,
                forConfirmation: true,
            }),
        [summary, internalTransfer, isCVU, transferGroup],
    );

    const notes = useMemo(() => getTransferConfirmationNotes(summary, internalTransfer, accounts), [
        summary,
        internalTransfer,
        accounts,
    ]);

    const titleHintText = useMemo(() => {
        if (!summary?.control) {
            return undefined;
        }
        const text = configUtils.get("frontend.mobileSignatureControl.text", "");
        return text || undefined;
    }, [summary?.control]);

    const subtitle = useMemo(
        () => (isCancelFlow ? i18n.get("cancel.check.transaction") : i18n.get("transfers.summary.checkInfo.label")),
        [isCancelFlow],
    );

    useEffect(() => {
        if (!isUxDashboardImprovementsEnabled()) {
            return undefined;
        }
        dispatchV3ShellLayout(dispatch, {
            headerTitle: title,
            titleKey: "",
            goBackPath,
            scopes: isCancelFlow ? ["cancelTransaction", "form"] : TRANSFER_CONFIRMATION_SCOPES,
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
        };
    }, [dispatch, title, goBackPath, isCancelFlow]);

    const handleGoBack = useCallback(() => {
        dispatch(replace(goBackPath));
    }, [dispatch, goBackPath]);

    const getToken = useCallback(async () => {
        if (isMobileWithToken && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    }, [isMobileWithToken, tokenNumber]);

    /**
     * Submits through the same actions as legacy. Deferred transfers (TAD) are sent under the
     * `.tad` variant of the activity id.
     *
     * The transfer sagas were written against a `withFormik` HOC and read connected props off
     * `formikBag.props`, which the kit's plain Formik bag does not carry. The bag is extended with
     * that shim so the sagas keep working untouched.
     */
    const handleConfirm = useCallback(
        (values, formikBag) => {
            const otp = values?.otp;
            const tadSuffix = summary?.tadTrx ? ".tad" : "";
            const legacyFormikBag = { ...formikBag, props: { userId, summary, envName, envDocument } };
            const payload = { ...summary, userId, formikBag: legacyFormikBag };

            if (isSignFlow) {
                dispatch(
                    formActions.signTransaction({
                        credentials: { otp },
                        formikBag: legacyFormikBag,
                        idTransaction: signTransactionData?.idTransaction ?? summary?.idTransaction,
                        idForm: signTransactionData?.idForm ?? "",
                        idActivity: signTransactionData?.idActivity ?? summary?.idActivity,
                        userId,
                        pendingDispatch: signTransactionData?.pendingDispatch ?? summary?.pendingDispatch,
                        useTransactionData: signTransactionData?.useTransactionData,
                    }),
                );
                return;
            }

            if (isCancelFlow) {
                dispatch(
                    formActions.cancelTransaction({
                        credentials: { otp },
                        formikBag: legacyFormikBag,
                        idTransaction: cancelTransactionData?.idTransaction ?? summary?.idTransaction,
                        userId,
                        rejectedTransactions: false,
                        force: cancelTransactionData?.force,
                    }),
                );
                return;
            }

            if (transferGroup === "toContact") {
                dispatch(
                    transferActions.sendTransferToContact(
                        { ...payload, idActivity: `transfers.toContact${tadSuffix}.send` },
                        otp,
                    ),
                );
            } else if (internalTransfer) {
                dispatch(
                    transferActions.sendInternalTransfer(
                        { ...payload, idActivity: `transfers.internal${tadSuffix}.send` },
                        otp,
                    ),
                );
            } else {
                dispatch(
                    transferActions.sendExternalTransfer(
                        { ...payload, idActivity: `transfers.thirdParties${tadSuffix}.send` },
                        otp,
                    ),
                );
            }
        },
        [
            dispatch,
            summary,
            userId,
            envName,
            envDocument,
            internalTransfer,
            transferGroup,
            signTransactionData,
            cancelTransactionData,
            isSignFlow,
            isCancelFlow,
        ],
    );

    const confirmation = useMemo(
        () => ({
            handleConfirm,
            loading: Boolean(isSubmitting),
            isInactive: false,
            confirmationText: i18n.get(
                isTokenActive() ? "confirmation.withoutotp.text" : "transfer.summary.confirm.label",
            ),
            getToken,
        }),
        [handleConfirm, isSubmitting, getToken],
    );

    const isTokenPending = isMobileWithToken && (fetchingToken || !tokenNumber);

    if (!summary?.debitAccount) {
        return null;
    }

    if (isTokenPending) {
        return <Spinner data-testid={transfersTestIds.confirmation.loading} size="large" />;
    }

    return (
        <TransferConfirmation
            goBack={handleGoBack}
            title={title}
            subtitle={subtitle}
            titleHintText={titleHintText}
            getLabels={getLabels}
            transferData={transferData}
            notes={notes}
            confirmation={confirmation}
            formatSignatureDate={formatLegacyWebDateTimeString}
        />
    );
}

TransferConfirmationV3Page.propTypes = {
    dispatch: func.isRequired,
    summary: shape({}),
    internalTransfer: bool,
    transferGroup: string,
    isCVU: bool,
    userId: string,
    envName: string,
    envDocument: string,
    isSubmitting: bool,
    accounts: arrayOf(shape({})),
    signTransactionData: shape({}),
    cancelTransactionData: shape({}),
    location: shape({}),
    isDesktop: bool,
    tokenNumber: string,
    fetchingToken: bool,
};

TransferConfirmationV3Page.defaultProps = {
    summary: null,
    internalTransfer: false,
    transferGroup: "",
    isCVU: false,
    userId: "",
    envName: "",
    envDocument: "",
    isSubmitting: false,
    accounts: [],
    signTransactionData: null,
    cancelTransactionData: null,
    location: null,
    isDesktop: true,
    tokenNumber: null,
    fetchingToken: false,
};

const mapStateToProps = (state) => {
    const summary = transferSelectors.getSummary(state);
    const isSignOrCancel = summary?.action === "sign" || summary?.action === "cancel";
    return {
        summary,
        internalTransfer: transferSelectors.getInternalTransfer(state),
        transferGroup: transferSelectors.getTransferGroup(state),
        isCVU: transferSelectors.getIsCVU(state),
        userId: sessionSelectors.getUser(state)?.userId,
        envName: sessionSelectors.getActiveEnvironment(state)?.name,
        envDocument: sessionSelectors.getActiveEnvironment(state)?.clients?.[0]?.idClient,
        // form.fetching defaults to true (INITIAL_STATE / LOCATION_CHANGE). Only use it for
        // sign/cancel; normal send must rely on transfer.fetching alone.
        isSubmitting: isSignOrCancel ? formSelectors.getFetching(state) : transferSelectors.getFetching(state),
        accounts: transferSelectors.getAccounts(state),
        signTransactionData: formSelectors.getSignTransactionData(state),
        cancelTransactionData: formSelectors.getCancelTransactionData(state),
        tokenNumber: tokenSelectors.getTokenNumber(state),
        fetchingToken: tokenSelectors.getFetchingToken(state),
    };
};

export default compose(connect(mapStateToProps), resizableRoute)(TransferConfirmationV3Page);
